import {
  FormLabel,
  HStack,
  VStack,
  Select,
  Button,
  Flex,
  useToast,
} from '@chakra-ui/react';

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from 'urql';
import {
  AreaUnitSettingType,
  AreaUnitTypeEnum,
  LengthUnitSettingType,
  LengthUnitTypeEnum,
  Project,
  RoundingEnum,
  UnitSettingsType,
  UpdateProjectDocument,
  VolumeUnitSettingType,
  VolumeUnitTypeEnum,
} from '../../gql/graphql';
import SparkelIcon from '../common/icon/SparkelIcon';

type UnitSettingsTabProps = {
  project: Pick<Project, 'id' | 'name' | 'settings'>;
};

const DEFAULT_UNITS_SETTINGS = {
  length: {
    units: LengthUnitTypeEnum.Meters,
    rounding: RoundingEnum['2Decimals'],
  } as LengthUnitSettingType,
  area: {
    units: AreaUnitTypeEnum.MetersSquared,
    rounding: RoundingEnum['2Decimals'],
  } as AreaUnitSettingType,
  volume: {
    units: VolumeUnitTypeEnum.MetersCubed,
    rounding: RoundingEnum['2Decimals'],
  } as VolumeUnitSettingType,
} as UnitSettingsType;

export default function UnitSettingsTab({
  project,
}: UnitSettingsTabProps): React.ReactElement {
  const { t } = useTranslation('company');
  const toast = useToast();

  const initialSettings = useMemo(
    () => project?.settings?.unitSettings || DEFAULT_UNITS_SETTINGS,
    [project]
  );

  const [unitSettings, setUnitSettings] =
    useState<UnitSettingsType>(initialSettings);

  const [isLoading, setIsLoading] = useState(false);

  const changesMade = useMemo(
    () => JSON.stringify(initialSettings) !== JSON.stringify(unitSettings),
    [initialSettings, unitSettings]
  );

  const [, updateProject] = useMutation(UpdateProjectDocument);

  const saveChanges = useCallback(async () => {
    setIsLoading(true);

    const result = await updateProject({
      input: {
        id: project.id,
        patch: {
          settings: {
            ...project.settings,
            unitSettings,
          },
        },
      },
    });

    if (result.error) {
      toast({
        title: t('project-settings.units.error.update'),
        status: 'error',
      });
      throw Error();
    } else {
      toast({
        title: t('project-settings.units.success.update'),
        status: 'success',
      });
    }

    setIsLoading(false);
  }, [project.id, project.settings, toast, unitSettings, updateProject]);

  return (
    <Flex height={'100%'} direction={'column'}>
      <VStack alignItems={'start'} gap={0} marginBottom={4}>
        <FormLabel>{t('project-settings.units.length-units')}</FormLabel>
        <HStack paddingLeft={2}>
          <VStack alignItems={'start'} gap={0}>
            <FormLabel fontSize={'sm'} color={'gray.500'}>
              {t('project-settings.units.units')}
            </FormLabel>
            <Select
              variant={'filled'}
              width={200}
              borderRadius={'md'}
              defaultValue={
                unitSettings.length?.units || LengthUnitTypeEnum.Meters
              }
              onChange={(e) =>
                setUnitSettings({
                  ...unitSettings,
                  length: {
                    ...unitSettings.length,
                    units: e.target.value as LengthUnitTypeEnum,
                  },
                })
              }
              disabled={isLoading}
            >
              <option value={LengthUnitTypeEnum.Millimeters}>
                {t('project-settings.units.millimeters')}
              </option>
              <option value={LengthUnitTypeEnum.Centimeters}>
                {t('project-settings.units.centimeters')}
              </option>
              <option value={LengthUnitTypeEnum.Decimeters}>
                {t('project-settings.units.decimeters')}
              </option>
              <option value={LengthUnitTypeEnum.Meters}>
                {t('project-settings.units.meters')}
              </option>
              <option value={LengthUnitTypeEnum.DecimalFeet}>
                {t('project-settings.units.decimal-feet')}
              </option>
              <option value={LengthUnitTypeEnum.DecimalInches}>
                {t('project-settings.units.decimal-inches')}
              </option>
            </Select>
          </VStack>
          <VStack alignItems={'start'} gap={0}>
            <FormLabel fontSize={'sm'} color={'gray.500'}>
              {t('project-settings.units.precision')}
            </FormLabel>
            <Select
              variant={'filled'}
              width={150}
              borderRadius={'md'}
              defaultValue={
                unitSettings.length?.rounding || RoundingEnum['2Decimals']
              }
              onChange={(e) =>
                setUnitSettings({
                  ...unitSettings,
                  length: {
                    ...unitSettings.length,
                    rounding: e.target.value as RoundingEnum,
                  },
                })
              }
              disabled={isLoading}
            >
              <option value={RoundingEnum.Nearest_1000}>
                {t('project-settings.units.nearest-1000')}
              </option>
              <option value={RoundingEnum.Nearest_100}>
                {t('project-settings.units.nearest-100')}
              </option>
              <option value={RoundingEnum.Nearest_10}>
                {t('project-settings.units.nearest-10')}
              </option>
              <option value={RoundingEnum['0Decimals']}>
                {t('project-settings.units.0-decimals')}
              </option>
              <option value={RoundingEnum['1Decimals']}>
                {t('project-settings.units.1-decimal')}
              </option>
              <option value={RoundingEnum['2Decimals']}>
                {t('project-settings.units.2-decimals')}
              </option>
              <option value={RoundingEnum['3Decimals']}>
                {t('project-settings.units.3-decimals')}
              </option>
            </Select>
          </VStack>
        </HStack>
      </VStack>
      <VStack alignItems={'start'} gap={0} marginBottom={4}>
        <FormLabel>{t('project-settings.units.area-units')}</FormLabel>
        <HStack paddingLeft={2}>
          <VStack alignItems={'start'} gap={0}>
            <FormLabel fontSize={'sm'} color={'gray.500'}>
              {t('project-settings.units.units')}
            </FormLabel>
            <Select
              variant={'filled'}
              width={200}
              borderRadius={'md'}
              defaultValue={
                unitSettings.area?.units || AreaUnitTypeEnum.MetersSquared
              }
              onChange={(e) =>
                setUnitSettings({
                  ...unitSettings,
                  area: {
                    ...unitSettings.area,
                    units: e.target.value as AreaUnitTypeEnum,
                  },
                })
              }
              disabled={isLoading}
            >
              <option value={AreaUnitTypeEnum.MillimetersSquared}>
                {t('project-settings.units.square-millimeters')}
              </option>
              <option value={AreaUnitTypeEnum.CentimetersSquared}>
                {t('project-settings.units.square-centimeters')}
              </option>
              <option value={AreaUnitTypeEnum.DecimetersSquared}>
                {t('project-settings.units.square-decimeters')}
              </option>
              <option value={AreaUnitTypeEnum.MetersSquared}>
                {t('project-settings.units.square-meters')}
              </option>
              <option value={AreaUnitTypeEnum.DecimalFeetSquared}>
                {t('project-settings.units.square-decimal-feet')}
              </option>
              <option value={AreaUnitTypeEnum.DecimalInchesSquared}>
                {t('project-settings.units.square-decimal-inches')}
              </option>
            </Select>
          </VStack>
          <VStack alignItems={'start'} gap={0}>
            <FormLabel fontSize={'sm'} color={'gray.500'}>
              {t('project-settings.units.precision')}
            </FormLabel>
            <Select
              variant={'filled'}
              width={150}
              borderRadius={'md'}
              defaultValue={
                unitSettings.area?.rounding || RoundingEnum['2Decimals']
              }
              onChange={(e) =>
                setUnitSettings({
                  ...unitSettings,
                  area: {
                    ...unitSettings.area,
                    rounding: e.target.value as RoundingEnum,
                  },
                })
              }
              disabled={isLoading}
            >
              <option value={RoundingEnum.Nearest_1000}>
                {t('project-settings.units.nearest-1000')}
              </option>
              <option value={RoundingEnum.Nearest_100}>
                {t('project-settings.units.nearest-100')}
              </option>
              <option value={RoundingEnum.Nearest_10}>
                {t('project-settings.units.nearest-10')}
              </option>
              <option value={RoundingEnum['0Decimals']}>
                {t('project-settings.units.0-decimals')}
              </option>
              <option value={RoundingEnum['1Decimals']}>
                {t('project-settings.units.1-decimal')}
              </option>
              <option value={RoundingEnum['2Decimals']}>
                {t('project-settings.units.2-decimals')}
              </option>
              <option value={RoundingEnum['3Decimals']}>
                {t('project-settings.units.3-decimals')}
              </option>
            </Select>
          </VStack>
        </HStack>
      </VStack>
      <VStack alignItems={'start'} gap={0}>
        <FormLabel>{t('project-settings.units.volume-units')}</FormLabel>
        <HStack paddingLeft={2}>
          <VStack alignItems={'start'} gap={0}>
            <FormLabel fontSize={'sm'} color={'gray.500'}>
              {t('project-settings.units.units')}
            </FormLabel>
            <Select
              variant={'filled'}
              width={200}
              borderRadius={'md'}
              defaultValue={
                unitSettings.volume?.units || VolumeUnitTypeEnum.MetersCubed
              }
              onChange={(e) =>
                setUnitSettings({
                  ...unitSettings,
                  volume: {
                    ...unitSettings.volume,
                    units: e.target.value as VolumeUnitTypeEnum,
                  },
                })
              }
              disabled={isLoading}
            >
              <option value={VolumeUnitTypeEnum.MillimetersCubed}>
                {t('project-settings.units.cubic-millimeters')}
              </option>
              <option value={VolumeUnitTypeEnum.CentimetersCubed}>
                {t('project-settings.units.cubic-centimeters')}
              </option>
              <option value={VolumeUnitTypeEnum.DecimetersCubed}>
                {t('project-settings.units.cubic-decimeters')}
              </option>
              <option value={VolumeUnitTypeEnum.MetersCubed}>
                {t('project-settings.units.cubic-meters')}
              </option>
              <option value={VolumeUnitTypeEnum.DecimalFeetCubed}>
                {t('project-settings.units.cubic-decimal-feet')}
              </option>
              <option value={VolumeUnitTypeEnum.DecimalInchesCubed}>
                {t('project-settings.units.cubic-decimal-inches')}
              </option>
            </Select>
          </VStack>
          <VStack alignItems={'start'} gap={0}>
            <FormLabel fontSize={'sm'} color={'gray.500'}>
              {t('project-settings.units.precision')}
            </FormLabel>
            <Select
              variant={'filled'}
              width={150}
              borderRadius={'md'}
              defaultValue={
                unitSettings.volume?.rounding || RoundingEnum['2Decimals']
              }
              onChange={(e) =>
                setUnitSettings({
                  ...unitSettings,
                  volume: {
                    ...unitSettings.volume,
                    rounding: e.target.value as RoundingEnum,
                  },
                })
              }
              disabled={isLoading}
            >
              <option value={RoundingEnum.Nearest_1000}>
                {t('project-settings.units.nearest-1000')}
              </option>
              <option value={RoundingEnum.Nearest_100}>
                {t('project-settings.units.nearest-100')}
              </option>
              <option value={RoundingEnum.Nearest_10}>
                {t('project-settings.units.nearest-10')}
              </option>
              <option value={RoundingEnum['0Decimals']}>
                {t('project-settings.units.0-decimals')}
              </option>
              <option value={RoundingEnum['1Decimals']}>
                {t('project-settings.units.1-decimal')}
              </option>
              <option value={RoundingEnum['2Decimals']}>
                {t('project-settings.units.2-decimals')}
              </option>
              <option value={RoundingEnum['3Decimals']}>
                {t('project-settings.units.3-decimals')}
              </option>
            </Select>
          </VStack>
        </HStack>
      </VStack>
      {changesMade && (
        <Button
          variant={'outline'}
          leftIcon={<SparkelIcon icon={faCheck} color={'inherit'} />}
          marginLeft={'auto'}
          marginTop={8}
          onClick={saveChanges}
          isLoading={isLoading}
        >
          {t('project-settings.units.save-changes')}
        </Button>
      )}
    </Flex>
  );
}
